import React, {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import character1 from "../../../assets/characters/character1.png";
import { ReactComponent as PrevIcon } from "../../../assets/icons/carousel-left.svg";
import { ReactComponent as NextIcon } from "../../../assets/icons/carousel-right.svg";
import useResponsive from "../../../services/useResponsive";
import { RenderSmoothImage } from "../../../components/RenderSmoothImage";
import { CharacterCard } from "../PraxisPlayHCPScreen";
import { getPraxisPlayViewportMetrics } from "../praxisPlayViewport";

const heightPoint = 480 / 950;
const widthPoint = 226 / 1440;

const dataLoading = [
  {
    isLoading: true,
    cover: character1,
  },
  {
    isLoading: true,
    cover: character1,
  },
  {
    isLoading: true,
    cover: character1,
  },
  {
    isLoading: true,
    cover: character1,
  },
  {
    isLoading: true,
    cover: character1,
  },
  {
    isLoading: true,
    cover: character1,
  },
  {
    isLoading: true,
    cover: character1,
  },
];

const calculateImageWidthAndHeight = (innerHeight, innerWidth) => {
  // for 1440 x 950 screen image width is 226px and height is 360px
  // based on this we can calculate the width and height for any screen size

  const height = heightPoint * innerHeight;
  const width = widthPoint * innerWidth;
  return { height, width };
};

export const Card = React.memo(function (props: any) {
  const { data, dataIndex } = props;
  const { cover, isLoading } = data[dataIndex];
  const [height, setHeight] = React.useState(heightPoint * window.innerHeight);
  const { isMobile } = useResponsive();

  useEffect(() => {
    const handleResize = () => {
      const { height } = calculateImageWidthAndHeight(
        window.innerHeight,
        window.innerWidth
      );
      setHeight(height);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div
      style={{
        width: "100%",
        height: isMobile ? "380px" : height + "px",
        userSelect: "none",
      }}
      className="my-slide-component"
    >
      {isLoading ? (
        <div className="smooth-preloader">
          <span
            style={{
              height: "100%",
              width: "100%",
              objectFit: "cover",
              borderRadius: "12px",
            }}
            className={`loader`}
          />
        </div>
      ) : (
        <RenderSmoothImage
          imageProps={{
            style: {
              height: "100%",
              width: "100%",
              objectFit: "cover",
              borderRadius: "12px",
              backgroundColor: "#F0ECEC",
            },
            draggable: false,
          }}
          src={cover}
          alt="character"
        />
      )}
    </div>
  );
});

const SlideContext = createContext<Record<string, any>>({});

const CarouselSlide = React.memo(function CarouselSlide(props: any) {
  const { data, dataIndex, slideIndex, isCenterSlide, swipeTo } = props;
  const ctx = useContext(SlideContext);
  const raw = data[dataIndex];
  if (!raw) return null;

  const { isLastHCP: _formLastHcp, ...formWithoutLastHcp } = ctx.form ?? {};
  const character =
    dataIndex === ctx.selectedCharacter
      ? { ...raw, ...formWithoutLastHcp }
      : raw;

  return (
    <div style={{ width: "100%", height: ctx.cardHeight, userSelect: "none" }}>
      <CharacterCard
        character={character}
        selectedCharacter={ctx.selectedCharacter}
        setSelectedCharacter={() => {
          if (!isCenterSlide) swipeTo(slideIndex);
          else ctx.onSelect?.(dataIndex);
        }}
        setForm={ctx.setForm}
        index={dataIndex}
        tenantProfile={ctx.tenantProfile}
        group={ctx.group}
        objections={ctx.objections}
        hcpTag={ctx.hcpTag}
        cardWidth={ctx.cardWidth}
        cardHeight={ctx.cardHeight}
        scale={ctx.scale}
      />
    </div>
  );
});

const CUSTOM_SCALES = [1, 0.85, 0.8, 0.65];
/** Default gap between adjacent card edges at reference scale. */
const CARD_GAP_PX = 20;
const TRANSITION_MS = 450;
const CAROUSEL_WIDTH_EXPAND_FACTOR = 1.4;
const CHARACTER_CARD_WIDTH = 275;
const CARD_MIN_VISIBLE_HEIGHT = 420;

interface CarouselHandle {
  goNext: () => void;
  goBack: () => void;
}

const FiniteStackedCarousel = forwardRef<
  CarouselHandle,
  {
    data: any[];
    slideWidth: number;
    height: number;
    carouselWidth: number;
    slideComponent: React.ComponentType<any>;
    onActiveSlideChange?: (index: number) => void;
    customScales: number[];
    activeIndex?: number;
    cardGap?: number;
  }
>(function FiniteStackedCarousel(
  {
    data,
    slideWidth,
    height,
    carouselWidth,
    slideComponent: SlideComponent,
    onActiveSlideChange,
    customScales,
    activeIndex: controlledIndex,
    cardGap = CARD_GAP_PX,
  },
  ref
) {
  const [internalIndex, setInternalIndex] = useState(0);
  const isControlled = controlledIndex !== undefined;
  const activeIndex = isControlled ? controlledIndex : internalIndex;
  const activeRef = useRef(activeIndex);
  activeRef.current = activeIndex;
  const touchStartX = useRef(0);

  useEffect(() => {
    if (!isControlled) {
      setInternalIndex((prev) =>
        Math.min(prev, Math.max(0, data.length - 1))
      );
    }
  }, [data.length, isControlled]);

  const navigate = useCallback(
    (target: number) => {
      const clamped = Math.max(0, Math.min(target, data.length - 1));
      if (!isControlled) setInternalIndex(clamped);
      onActiveSlideChange?.(clamped);
    },
    [data.length, onActiveSlideChange, isControlled]
  );

  useImperativeHandle(
    ref,
    () => ({
      goNext: () => navigate(activeRef.current + 1),
      goBack: () => navigate(activeRef.current - 1),
    }),
    [navigate]
  );

  const maxLevels = customScales.length;

  return (
    <div
      style={{
        width: carouselWidth,
        height,
        position: "relative",
        overflow: "hidden",
      }}
      onTouchStart={(e) => {
        touchStartX.current = e.touches[0].clientX;
      }}
      onTouchEnd={(e) => {
        const diff = e.changedTouches[0].clientX - touchStartX.current;
        if (diff > 50) navigate(activeRef.current - 1);
        else if (diff < -50) navigate(activeRef.current + 1);
      }}
    >
      {data.map((_, index) => {
        const distance = index - activeIndex;
        const absDistance = Math.abs(distance);
        if (absDistance >= maxLevels) return null;

        const scale = customScales[absDistance];
        const direction = Math.sign(distance);

        let position = 0;
        for (let d = 1; d <= absDistance; d++) {
          position +=
            slideWidth * (customScales[d - 1] + customScales[d]) / 2 +
            cardGap;
        }
        position *= direction;

        return (
          <div
            key={index}
            style={{
              position: "absolute",
              display: "flex",
              left: `calc(50% - ${slideWidth / 2}px)`,
              top: "50%",
              transform: `translateX(${position}px) translateY(-50%) scale(${scale})`,
              width: slideWidth,
              transition: `all ${TRANSITION_MS}ms ease`,
              zIndex: maxLevels - absDistance,
            }}
          >
            <SlideComponent
              data={data}
              dataIndex={index}
              slideIndex={distance}
              isCenterSlide={index === activeIndex}
              swipeTo={(steps: number) => navigate(activeRef.current + steps)}
            />
          </div>
        );
      })}
    </div>
  );
});

export function ResponsiveCarousel({
  onChange,
  data = [],
  isLoading,
  slideContext,
}: {
  onChange: (index: number) => void;
  data: any[];
  isLoading: boolean;
  slideContext?: Record<string, any>;
}) {
  const ref = useRef<CarouselHandle>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const heightRatio = 480 / 950;
  const [height, setHeight] = useState(heightRatio * window.innerHeight);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const { isMobile } = useResponsive();

  const itemCount = isLoading ? dataLoading.length : data.length;
  const isCharacterCarousel = Boolean(slideContext && !isLoading);
  const baseCarouselHeight = isMobile
    ? 380
    : isCharacterCarousel
      ? Math.max(CARD_MIN_VISIBLE_HEIGHT, height)
      : height;

  // Card scaling is driven by the actual window size (not the container) so it
  // only grows above the ~16" threshold defined in getPraxisPlayViewportMetrics.
  // On mobile we still need the metrics (fixed width 150 / scale 0.85) so the
  // scale-based sizing in CharacterCard shrinks correctly instead of falling
  // back to the desktop defaults.
  const viewportMetrics = useMemo(() => {
    if (!isCharacterCarousel) {
      return null;
    }
    return getPraxisPlayViewportMetrics(windowSize.width, windowSize.height, {
      isMobile,
    });
  }, [isCharacterCarousel, isMobile, windowSize.width, windowSize.height]);

  const carouselHeight = isMobile
    ? 380
    : viewportMetrics?.carouselHeight ?? baseCarouselHeight;
  const slideW = isCharacterCarousel
    ? viewportMetrics?.characterCardWidth ?? CHARACTER_CARD_WIDTH
    : isMobile
      ? 150
      : carouselHeight / 1.6;
  const cardGap = viewportMetrics?.cardGap ?? CARD_GAP_PX;
  const cardWidth = viewportMetrics?.characterCardWidth ?? CHARACTER_CARD_WIDTH;
  const cardHeight = viewportMetrics?.characterCardHeight ?? carouselHeight;
  const cardScale = viewportMetrics?.scale ?? 1;
  // Vertical breathing room around the (centered) card so the "Last Selected
  // HCP" badge that sits above the card top is never clipped, and the selected
  // card's top edge isn't cut off against the stage's overflow:hidden.
  const badgeRoom = isCharacterCarousel ? Math.round(30 * cardScale) : 0;
  const stageHeight = isCharacterCarousel
    ? cardHeight + badgeRoom * 2
    : carouselHeight;

  const contextValue = useMemo(
    () => ({
      ...slideContext,
      onSelect: onChange,
      cardWidth,
      cardHeight,
      scale: cardScale,
    }),
    [slideContext, onChange, cardWidth, cardHeight, cardScale],
  );

  useEffect(() => {
    const handleResize = () => {
      const { height } = calculateImageWidthAndHeight(
        window.innerHeight,
        window.innerWidth
      );
      setHeight(height);
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    setContainerWidth(el.offsetWidth);
    const observer = new ResizeObserver((entries) => {
      setContainerWidth(entries[0].contentRect.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  if (!isLoading && data.length === 0) return null;

  const expandable = itemCount >= 3;
  const showNavButtons = itemCount > 1;
  const slideComponent = slideContext && !isLoading ? CarouselSlide : Card;
  const controlledIndex =
    slideContext && !isLoading ? slideContext.selectedCharacter : undefined;

  const shouldExpand = !isMobile && expandable && containerWidth > 0;
  const expandedWidth = shouldExpand
    ? CAROUSEL_WIDTH_EXPAND_FACTOR * (containerWidth + slideW)
    : containerWidth;
  const marginOffset = -(expandedWidth - containerWidth) / 2;

  return (
    <>
      {showNavButtons ? (
        <button
          className="-mr-6 hover:opacity-70 z-[1]"
          onClick={() => ref.current?.goBack()}
        >
          <PrevIcon />
        </button>
      ) : (
        <div />
      )}
      <SlideContext.Provider value={contextValue}>
        <div
          ref={containerRef}
          className="w-full relative"
          style={{
            width: "100%",
            position: "relative",
            height: isCharacterCarousel ? stageHeight + "px" : undefined,
            maxHeight: stageHeight + "px",
            overflow: "hidden",
          }}
        >
          {containerWidth > 0 && (
            <div
              style={
                shouldExpand
                  ? { width: expandedWidth, marginLeft: marginOffset }
                  : undefined
              }
            >
              <FiniteStackedCarousel
                ref={ref}
                slideComponent={slideComponent}
                slideWidth={slideW}
                carouselWidth={expandedWidth || containerWidth}
                data={isLoading ? [...dataLoading] : [...data]}
                customScales={CUSTOM_SCALES}
                height={stageHeight}
                cardGap={cardGap}
                activeIndex={controlledIndex}
                onActiveSlideChange={(index) => onChange(index)}
              />
            </div>
          )}
        </div>
      </SlideContext.Provider>
      {showNavButtons ? (
        <button
          className="-ml-6 hover:opacity-70 z-[1]"
          onClick={() => ref.current?.goNext()}
        >
          <NextIcon />
        </button>
      ) : (
        <div />
      )}
    </>
  );
}
